import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

// SERVICES
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from "ngx-spinner";
import { ApiService } from 'src/app/services/api.service';
import { ChangeDetectionService } from 'src/app/services/change-detection.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { PushNotificationService } from '../push-notification.service';



@Component({
  selector: 'app-features-push-notif-edit',
  templateUrl: './feature-push-notif-edit.component.html',
  styleUrls: ['./feature-push-notif-edit.component.scss']
})
export class FeaturesPushNotifEditComponent implements OnInit, OnDestroy {

  public updateForm: UntypedFormGroup;
  isLoading: boolean;
  uid: any;

  // images
  cover_img: any;
  feature_img: any;
  imageFile: {link: string, file: any, name: string};
  imageFile2: {link: string, file: any, name: string};

  // Store original values for change detection
  originalValues: any = {};
  
  // Original image URLs from server
  originalCoverImageUrl = '';
  originalFeatureImageUrl = '';
  
  // unsubscribe cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private apiService: ApiService,
    private changeDetectionService: ChangeDetectionService,
    private popUpDialogService: PopUpDialogService,
    private location: Location,
    private pushNotificationService: PushNotificationService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.uid = this.route.snapshot.paramMap.get('id');
    this.get_pushNotification().catch(() => {
      this.toastr.error(
        `Please refresh or contact the developer if the error still occurs`,
        'Something went wrong'
      );
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectFileCover(event) {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      const link = event.srcElement.files[0];
      const name = event.srcElement.files[0].name;
      reader.onload = (event: any) => {
        this.imageFile = {
          link: event.target.result,
          file: link,
          name
        };
        this.cover_img = event.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
    }
    console.log('New cover image selected:', event.srcElement.files[0].name);
  }

  onSelectFileFeatured(event) {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      const link = event.srcElement.files[0];
      const name = event.srcElement.files[0].name;
      reader.onload = (event: any) => {
        this.imageFile2 = {
          link: event.target.result,
          file: link,
          name
        };
        this.feature_img = event.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
    }
    console.log('New feature image selected:', event.srcElement.files[0].name);
  }

  initializeForm() {
    this.updateForm = this.fb.group({
      title: [''],
      message: ['']
    });

    // Subscribe to form changes
    this.updateForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Form changes are tracked, hasChanges() will detect them
    });
  }

  async get_pushNotification() {
    try {
      this.spinner.show().catch((err) => {
        console.error('Spinner show error:', err);
      });


      const response = await firstValueFrom(
        this.apiService.getNotificationById(this.uid)
      );



      if (response && response.result) {
        this.updateForm.patchValue({
          title: response.result.title || '',
          message: response.result.message || ''
        });
        
        
        // Store original images
        this.cover_img = response.result.notifCoverImageUrl;
        this.originalCoverImageUrl = response.result.notifCoverImageUrl;
        
        this.feature_img = response.result.notifFeatureImageUrl;
        this.originalFeatureImageUrl = response.result.notifFeatureImageUrl;
        
        
        // Store original values AFTER form is populated
        this.storeOriginalValues();
      } else {
        this.toastr.error('Failed to load push notification data', 'Error!');
      }

      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });

    } catch (error) {
      this.toastr.error('Failed to load push notification', 'Error!');
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

    cancel(): void {
    if (!this.hasChanges()) {
      this.location.back();
      return;
    }
    
    this.popUpDialogService.openInfoActionDialog(
      'You have unsaved changes. Are you sure you want to cancel?',
      () => {
        this.location.back();
        this.toastr.success('Changes discarded', 'Success');
      },
      () => {
        console.log('Cancel edit cancelled by user');
      }
    );
  }

  storeOriginalValues() {
    const valuesToStore = {
      title: this.updateForm.controls.title.value || '',
      message: this.updateForm.controls.message.value || '',
      coverImageUrl: this.originalCoverImageUrl,
      featureImageUrl: this.originalFeatureImageUrl,
      hasNewCoverImage: false,
      hasNewFeatureImage: false
    };

    // Use the service to create a deep clone
    this.originalValues = this.changeDetectionService.cloneValues(valuesToStore);
  }
  
  hasChanges(): boolean {
    const currentValues = this.getCurrentValues();
    const hasFormChanges = this.changeDetectionService.hasChanges(
      this.originalValues, 
      currentValues
    );
    
    return hasFormChanges;
  }

  getCurrentValues(): any {
    return {
      title: this.updateForm.controls.title.value || '',
      message: this.updateForm.controls.message.value || '',
      coverImageUrl: this.originalCoverImageUrl,
      featureImageUrl: this.originalFeatureImageUrl,
      hasNewCoverImage: !!this.imageFile?.file, 
      hasNewFeatureImage: !!this.imageFile2?.file 
    };
  }

  isSubmitDisabled(): boolean {
    return !this.hasChanges();
  }

  async update() {
    // Validate required fields
    const title = this.updateForm.controls.title.value;
    const message = this.updateForm.controls.message.value;

    if (!title || title.trim() === '') {
      this.toastr.error('Please enter Title', 'Required Field Missing');
      return;
    }

    if (!message || message.trim() === '') {
      this.toastr.error('Please enter Message', 'Required Field Missing');
      return;
    }

    if (!this.hasChanges()) {
      this.toastr.info('No changes detected', 'Info');
      return;
    }

    // Confirm before updating
    this.popUpDialogService.openSaveActionDialog(
      'Are you sure you want to update this push notification?',
      async () => {
        await this.pushNotificationService.performUpdate({
          title,
          message,
          coverImage: this.imageFile?.file || null,
          featureImage: this.imageFile2?.file || null,
          uid: this.uid
        });
      },
      () => {
        console.log('Update cancelled by user');
      }
    );
  }
}
